import { render } from 'preact'
import { ResultsCard } from 'libs/searchspring/components/ResultCard'
import clsx from 'clsx'
import { SearchspringSearch } from 'libs/searchspring/fetchers/search'
import { normaliseResults } from 'libs/searchspring/helpers/normalisers'

export class ProductUpsellCarousel extends HTMLElement {
  constructor () {
    super()
    this.products = []
    this.enrichedProducts = []
    this.carouselSettings = {}
    this.productCardSettings = {}
    this.limit = 8
  }

  async connectedCallback () {
    this.parseAttributes()
    await this.enrichProductsWithSearchspringData()
    this.renderCarousel()
  }

  parseAttributes () {
    const productsData = this.getAttribute('data-products')
    if (productsData) {
      try {
        this.products = JSON.parse(productsData).slice(0, this.limit)
      } catch (e) {
        console.error('Failed to parse products data:', e)
        this.products = []
      }
    }

    const carouselSettings = this.getAttribute('carousel-settings')
    if (carouselSettings) {
      try {
        this.carouselSettings = JSON.parse(carouselSettings)
      } catch (e) {
        console.error('Failed to parse carousel settings:', e)
      }
    }

    const productCardSettings = this.getAttribute('product-card-settings')
    if (productCardSettings) {
      try {
        this.productCardSettings = JSON.parse(productCardSettings)
      } catch (e) {
        console.error('Failed to parse product card settings:', e)
        this.productCardSettings = {}
      }
    }

    const limit = this.getAttribute('data-limit')
    if (limit) {
      this.limit = parseInt(limit, 10) || 8
    }
  }

  async enrichProductsWithSearchspringData () {
    if (!this.products.length) {
      this.enrichedProducts = []
      return
    }

    try {
      const handles = this.products.map(product => product.handle)
      const fetcher = new SearchspringSearch()
      fetcher.updateParams({
        query: '*',
        resultsPerPage: this.limit
      })

      handles.forEach(handle => {
        fetcher.params.append('filter.handle', handle)
      })

      const searchResults = await fetcher.fetch()
      const normalizedResults = await normaliseResults(searchResults.results || [])
      const searchspringMap = new Map()
      normalizedResults.forEach(result => {
        if (result.handle) {
          searchspringMap.set(result.handle, result)
        }
      })

      this.enrichedProducts = this.products.map(shopifyProduct => {
        const searchspringData = searchspringMap.get(shopifyProduct.handle)
        if (searchspringData) {
          return {
            ...searchspringData,
            shopifyVariants: shopifyProduct.variants,
            available: shopifyProduct.available,
            shopifyData: shopifyProduct
          }
        } else {
          console.warn(`Product ${shopifyProduct.handle} not found in SearchSpring, using Shopify data`)
          return shopifyProduct
        }
      })
    } catch (error) {
      console.error('Failed to fetch SearchSpring data for products:', error)
      this.enrichedProducts = this.products
    }
  }

  renderCarousel () {
    if (!this.enrichedProducts.length) {
      this.innerHTML = ''
      return
    }

    const transformedProducts = this.enrichedProducts
    const carouselItems = transformedProducts.map((result, index) => (
      <div className='mr-carousel flex flex-col' key={result.handle} style={{ width: 'var(--carousel-columns)' }}>
        <ResultsCard
          result={result}
          className='wk-wishlist-button-target'
          dataIndex={index.toString()}
          options={this.productCardSettings}
        />
      </div>
    ))

    const CarouselWrapper = () => (
      <x-carousel
        className={clsx(
          'overflow-visible flex w-full children:shrink-0 md-max:[&_.flickity-page-dots]:w-full-marginless',
          this.className
        )}
        style={{ gap: 'var(--carousel-gap)' }}
        carousel-settings={JSON.stringify(this.carouselSettings)}
      >
        {carouselItems}
      </x-carousel>
    )

    render(<CarouselWrapper />, this)
    this.initializeCarousel()
  }

  initializeCarousel () {
    const carousel = this.querySelector('x-carousel')
    if (carousel && carousel.connectedCallback) {
      // Force initialization if needed
      setTimeout(() => {
        if (typeof carousel.initializeFlickity === 'function') {
          carousel.initializeFlickity()
        }
      }, 100)
    }
  }

  disconnectedCallback () {
    const carousel = this.querySelector('x-carousel')
    if (carousel && typeof carousel.destroy === 'function') {
      carousel.destroy()
    }
  }
}
